/**
 * Refund Policy Page
 * Refund terms for Kata.fit subscriptions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const RefundPolicyPage = () => {
  const effectiveDate = 'February 1, 2026';
  const lastUpdated = 'February 1, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <Link to="/" className="back-link">← Back to App</Link>
          <h1>Refund Policy</h1>
          <p className="meta">Effective Date: {effectiveDate} | Last Updated: {lastUpdated}</p>
        </header>

        <section className="policy-summary">
          <div className="highlight-box">
            <h3>📋 Quick Summary</h3>
            <ul>
              <li>✓ 14-day money-back guarantee on first subscription</li>
              <li>✓ No questions asked for first refund request</li>
              <li>✓ Pro-rated refunds for annual plans in first 30 days</li>
              <li>✓ Cancel anytime—no refund needed, just stop billing</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>1. Our Commitment</h2>
          <p>
            We want you to love Kata.fit Pro. If you're not satisfied with your subscription, 
            we offer a fair and straightforward refund policy. Our goal is to ensure you feel 
            confident trying our Pro features.
          </p>
        </section>

        <section>
          <h2>2. First-Time Subscriber Guarantee</h2>
          <p>
            If this is your first time subscribing to Kata.fit Pro, you're eligible for our 
            <strong> 14-day money-back guarantee</strong>:
          </p>
          <ul>
            <li>Request a full refund within 14 days of your first payment</li>
            <li>No questions asked—we'll process your refund promptly</li>
            <li>Simply contact us at team@kata.fit with your request</li>
          </ul>
          <p>
            This guarantee applies once per user. If you've previously received a refund 
            or used Pro features before, standard refund terms apply.
          </p>
        </section>

        <section>
          <h2>3. Monthly Subscriptions</h2>
          <p>For monthly Pro subscriptions:</p>
          <ul>
            <li><strong>First month:</strong> Full refund within 14 days (first-time guarantee)</li>
            <li><strong>Subsequent months:</strong> No partial refunds for the current billing period</li>
            <li><strong>Cancellation:</strong> Cancel anytime to stop future charges—you keep access 
            until the end of your current billing period</li>
          </ul>
          <p>
            We don't pro-rate monthly subscriptions because you can cancel at any time without penalty.
          </p>
        </section>

        <section>
          <h2>4. Annual Subscriptions</h2>
          <p>For annual Pro subscriptions:</p>
          <ul>
            <li><strong>First 14 days:</strong> Full refund (first-time guarantee)</li>
            <li><strong>Days 15-30:</strong> Pro-rated refund minus one month at the monthly rate</li>
            <li><strong>After 30 days:</strong> No refunds, but you can cancel to prevent renewal</li>
          </ul>
          <p>
            Annual plans offer significant savings, so we encourage trying monthly first if you're unsure.
          </p>
        </section>

        <section>
          <h2>5. How to Request a Refund</h2>
          <p>To request a refund:</p>
          <ol>
            <li>
              <strong>Email us:</strong> Send a refund request to{' '}
              <a href="mailto:team@kata.fit">team@kata.fit</a>
            </li>
            <li>
              <strong>Include:</strong> Your account email and reason for refund (optional but helpful)
            </li>
            <li>
              <strong>Wait:</strong> We'll process your request within 3-5 business days
            </li>
          </ol>
          <p>
            Refunds are processed through Paddle, our payment provider. Depending on your payment 
            method, it may take 5-10 additional business days for the refund to appear on your statement.
          </p>
        </section>

        <section>
          <h2>6. Non-Refundable Situations</h2>
          <p>We cannot provide refunds in the following situations:</p>
          <ul>
            <li>Requests made after the refund eligibility period</li>
            <li>Accounts terminated for Terms of Service violations</li>
            <li>Repeated refund requests (more than one per user)</li>
            <li>Chargebacks initiated without contacting us first</li>
            <li>Free tier users (no charges to refund)</li>
          </ul>
        </section>

        <section>
          <h2>7. What Happens After a Refund</h2>
          <p>After your refund is processed:</p>
          <ul>
            <li>Your account will be downgraded to the Free tier</li>
            <li>Pro features will no longer be accessible</li>
            <li>Your workout history, data, and account remain intact</li>
            <li>AI recommendations generated while Pro remain viewable</li>
            <li>You can re-subscribe at any time</li>
          </ul>
        </section>

        <section>
          <h2>8. Cancellation vs. Refund</h2>
          <p>
            <strong>Cancellation</strong> and <strong>refund</strong> are different:
          </p>
          <ul>
            <li>
              <strong>Cancellation:</strong> Stops future billing. You keep Pro access until 
              your current period ends. No money is returned.
            </li>
            <li>
              <strong>Refund:</strong> Returns your payment. Pro access ends immediately.
            </li>
          </ul>
          <p>
            If you just want to stop paying, cancel your subscription instead of requesting a refund. 
            You can manage your subscription through the Paddle customer portal in the app.
          </p>
        </section>

        <section>
          <h2>9. Technical Issues</h2>
          <p>
            If you experience technical issues that prevent you from using Pro features:
          </p>
          <ul>
            <li>Contact us at team@kata.fit so we can help resolve the issue</li>
            <li>We may extend your subscription at no cost while issues are resolved</li>
            <li>Persistent unresolvable issues may qualify for a full or partial refund</li>
          </ul>
        </section>

        <section>
          <h2>10. Payment Disputes</h2>
          <p>
            If you don't recognize a charge, please contact us before disputing with your bank. 
            We're happy to help investigate and resolve billing issues. Chargebacks initiated 
            without contacting us may result in account suspension.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Refund Policy. Changes won't affect refund requests already in 
            progress. We'll notify you of significant changes via email or in-app notification.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            For refund requests or billing questions:
          </p>
          <p>
            <strong>Email:</strong> team@kata.fit<br />
            <strong>Response time:</strong> 1-2 business days
          </p>
          <p>
            We're here to help! Don't hesitate to reach out if you have any concerns.
          </p>
        </section>

        <footer className="legal-footer">
          <div className="footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms-and-conditions">Terms & Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/support">Support</Link>
          </div>
          <p className="copyright">© {new Date().getFullYear()} Kata.fit. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
